import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';

const Services = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const services = [
    {
      icon: 'https://www.audioxlmilano.com/assets/AXL_icon-1_1.png',
      title: 'SOUND DESIGN',
      description: 'We record and create our own sounds to make every project unique.',
    },
    {
      icon: 'https://www.audioxlmilano.com/assets/AXL_icon-2_1.png',
      title: 'MIX & MASTER',
      description: 'Our sound engineer will find the perfect balance for your music, providing industry-standard audio quality, ready for major distribution on all platforms.',
    },
    {
      icon: 'https://www.audioxlmilano.com/assets/AXL_icon-3_1.png',
      title: 'RECORDING',
      description: 'Vocals, instruments and live sessions recorded in our Milan studio, through a chain of vintage, analog and digital gear.',
    },
    {
      icon: 'https://www.audioxlmilano.com/assets/AXL_icon-4_1.png',
      title: 'MUSIC PRODUCTION',
      description: 'From the first idea to the final master: songs, soundtracks and jingles produced around your project.',
    },
    {
      icon: 'https://www.audioxlmilano.com/assets/AXL_icon-52-2zz.png',
      title: 'DOLBY ATMOS',
      description: 'A revolutionary audio format that allows artists to translate their own music in a 360° environment, creating an immersive audio experience.',
    },
    {
      icon: 'https://www.audioxlmilano.com/assets/SW_BIANCO.png',
      title: 'SOFTWARE DEVELOPMENT',
      description: 'We build custom software, websites, and AI agents — from lightning-fast sites to full-stack platforms and smart automations. Design-driven, secure, and scalable.',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  return (
    <section
      id="services"
      data-testid="services-section"
      ref={ref}
      className="relative py-32 md:py-48 px-6 md:px-12 lg:px-24 overflow-hidden"
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat scale-110"
          style={{
            backgroundImage: "url('/assets/services-bg.jpg')",
            filter: 'blur(8px)',
          }}
        />
        <div className="absolute inset-0 bg-black/70" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mb-16 md:mb-24"
        >
          <span className="font-heading font-bold text-sm tracking-[0.3em] text-white/40 uppercase">
            What We Do
          </span>
          <h2
            className="font-heading font-bold text-3xl md:text-5xl lg:text-6xl leading-tight mt-6"
            data-testid="services-heading"
          >
            SERVICES
          </h2>
        </motion.div>

        {/* Services Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12"
        >
          {services.map((service, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="group p-8 bg-black/40 backdrop-blur-md border border-white/[0.1] hover:border-white/30 hover:bg-black/50 transition-all duration-500"
              data-testid={`service-item-${index}`}
            >
              {/* Icon */}
              <div className="w-full flex justify-center mb-8">
                <div className="w-16 h-16 relative">
                  <img
                    src={service.icon}
                    alt=""
                    className="w-full h-full object-contain opacity-70 group-hover:opacity-100 transition-opacity duration-500"
                  />
                </div>
              </div>

              {/* Title */}
              <h3 className="font-heading font-bold text-lg md:text-xl mb-4 text-center group-hover:text-white transition-colors duration-300">
                {service.title}
              </h3>

              {/* Description */}
              <p className="font-body text-sm md:text-base text-white/60 group-hover:text-white/80 leading-relaxed transition-colors duration-300 text-center">
                {service.description}
              </p>

              {/* Hover Line */}
              <div className="mt-8 h-[1px] bg-white/10 group-hover:bg-white/40 transition-colors duration-500" />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
